import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useState, useEffect } from 'react';
import financeCalculatorData from "@data/finance-calculator/finance_calculator_data.json";
var defaultState = {
  last_payment: undefined,
  registration_fees: 0,
  years: 5,
  monthly_installment: 0,
  total_price_with_our_profit: 0,
  //my values
  cash_price: 58000,
  down_payment: undefined,
  down_payment_percent: 0,
  period: 0,
  last_payment_percent: 0,
  profit_margin_percent: 0.077,
  profit_margin: 0,
  insurance_percent: 0,
  insurance_value: 0,
  bank_profit: 0,
  added_tax: 0,
  total_amount: 0,
  total_financing_amount: 0,
  total_financing_amount_without_down_payment: 0,
  total_car_price_without_tax: 0,
  tax_rate: 0.15,
  our_profit_percent: 0.07,
  purchase_price: 0,
  static_profit_percent: 0,
  max_down_payment: 7500,
  max_last_payment: 7500,
  max_down_payment_percent: 0.5,
  max_last_payment_percent: 0.45,
  car_condition: "used"
};
var InstallmentCalculatorContext = /*#__PURE__*/createContext(undefined);
export var InstallmentCalculatorProvider = function InstallmentCalculatorProvider(_ref) {
  var children = _ref.children,
    carCondition = _ref.carCondition,
    cashPrice = _ref.cashPrice,
    isPreowned = _ref.isPreowned,
    initialValues = _ref.initialValues,
    dynamicFinanceCalculatorData = _ref.dynamicFinanceCalculatorData;
  var calculateValues = function calculateValues(carCondition, cashPrice, financeCalculatorData) {
    var stateOverride = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    var dynamicValues = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
    var innerState = stateOverride || state;
    var values;
    // Determine which car condition data to use
    if (dynamicValues) {
      values = dynamicValues;
    } else {
      if (carCondition === 'used') {
        values = financeCalculatorData.used;
      } else if (carCondition === 'new') {
        values = financeCalculatorData["new"];
      } else if (carCondition === 'chinese') {
        values = financeCalculatorData.chinese;
      }
      // Merge the tax_rate into the values
      values = _objectSpread(_objectSpread({}, values), {}, {
        tax_rate: financeCalculatorData.tax_rate
      });
    }
    if (values) {
      var _values;
      var period = innerState.period !== 0 ? innerState.period : values.installment_period;
      var cash_price = cashPrice;
      var tax_rate = values.tax_rate;
      var our_profit_percent = values.our_profit_rate;
      // Calculate prices
      var total_car_price_without_tax = isPreowned ? cash_price : Math.round(cash_price / (1 + tax_rate));
      var profit_margin = ((_values = values) === null || _values === void 0 ? void 0 : _values.profit_margin) || 0;
      var total_car_price_with_our_profit = Math.round(total_car_price_without_tax * (1 + our_profit_percent)) + Number(profit_margin);
      var added_tax = Math.round(total_car_price_with_our_profit * tax_rate);
      var profit_margin_percent = values.profit_rate;
      var warranty = values.warranty || 0;
      var purchase_price = total_car_price_with_our_profit + added_tax;
      var insurance_percent = values.insurance;
      var insurance_value = Math.round(purchase_price * insurance_percent * (period / 12));
      var max_down_payment = Math.round(innerState.max_down_payment_percent * purchase_price);
      var max_last_payment = Math.round(innerState.max_last_payment_percent * purchase_price);
      var down_payment = typeof innerState.down_payment !== 'undefined' ? innerState.down_payment : Math.round(values.first_payment * purchase_price);
      var last_payment = typeof innerState.last_payment !== 'undefined' ? innerState.last_payment : Math.round(values.last_payment * purchase_price);
      var bank_profit = Math.round((purchase_price - down_payment) * profit_margin_percent * (period / 12));
      var total_financing_amount = purchase_price + insurance_value + bank_profit;
      var total_financing_amount_without_down_payment = total_financing_amount - down_payment + warranty;
      var monthly_installment = Math.ceil((total_financing_amount_without_down_payment - last_payment) / period);

      // Return all calculated values
      return {
        period: period,
        cash_price: cash_price,
        tax_rate: tax_rate,
        our_profit_percent: our_profit_percent,
        total_car_price_without_tax: total_car_price_without_tax,
        total_car_price_with_our_profit: total_car_price_with_our_profit,
        added_tax: added_tax,
        purchase_price: purchase_price,
        down_payment: down_payment,
        last_payment: last_payment,
        total_financing_amount: total_financing_amount,
        insurance_value: insurance_value,
        bank_profit: bank_profit,
        profit_margin_percent: profit_margin_percent,
        profit_margin: profit_margin,
        total_financing_amount_without_down_payment: total_financing_amount_without_down_payment,
        monthly_installment: monthly_installment,
        insurance_percent: insurance_percent,
        max_down_payment: max_down_payment,
        max_last_payment: max_last_payment
      };
    }
    return null;
  };
  var baseState = _objectSpread(_objectSpread({}, defaultState), calculateValues(carCondition, cashPrice, financeCalculatorData, defaultState, dynamicFinanceCalculatorData));
  var initialState = initialValues ? _objectSpread(_objectSpread({}, baseState), initialValues) : baseState;
  var _useState = useState(initialState),
    state = _useState[0],
    setState = _useState[1];
  useEffect(function () {
    var calculatedValues = calculateValues(carCondition, cashPrice, financeCalculatorData, null, dynamicFinanceCalculatorData);
    if (calculatedValues) {
      setState(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), calculatedValues);
      });
    }
  }, [carCondition, cashPrice, state.down_payment, state.last_payment, state.period, dynamicFinanceCalculatorData]);
  return /*#__PURE__*/React.createElement(InstallmentCalculatorContext.Provider, {
    value: {
      state: state,
      setState: setState
    }
  }, children);
};
export var useInstallmentCalculator = function useInstallmentCalculator() {
  var context = useContext(InstallmentCalculatorContext);
  if (!context) {
    throw new Error('useInstallmentCalculator must be used within an InstallmentCalculatorProvider');
  }
  return context;
};